/**
 * Interactive Tier Packages Widget - "Unlock Bundle Savings"
 * Replaces the old progress bar with clickable tier packages
 *
 * Features:
 * - 3 interactive tier cards (Value, Complete, Elite)
 * - Real pricing from product API with discounts
 * - "Learn More" x-modal for each tier
 * - "Add Bundle to Cart" adds parent + all tier addons
 * - Opens bundle drawer to configuration/summary screen
 * - Smart Elite package includes ALL lower tier items + premium exclusives
 */

(function(window) {
  'use strict';

  class TierProgressWidget extends window.ProductWidgets.BaseWidget {
    constructor(manager, container, config) {
      config.requiresBundleConfig = true; // NEW: Mark as requiring bundleConfig
      super(manager, container, config);

      this.lazyLoad = config.lazyLoad || false;
      this.packages = null;
      this.loadingProducts = false;

      // Track selected variant for image updates
      this.selectedVariantId = this.productData?.variants?.[0]?.id || null;

      // Highlight setting: which tier to feature with dark card
      // Options: 'none', 'value', 'complete', 'highest-savings', 'highest-price', 'position-1', 'position-2'
      this.highlightTier = config.highlightTier || 'none';

      // Expose highlight setting globally for bundle drawer to access
      window.bundleDrawerHighlightTier = this.highlightTier;

      // Button colors for highlighted card (from theme settings)
      this.buttonColors = {
        text: config.colorButtonText || '#171717',
        background: config.colorButtonBackground || '#ffffff',
        gradient: config.colorButtonGradient || 'linear-gradient(57deg, rgba(249, 119, 255, 1) 8%, rgba(54, 212, 255, 1) 47%, rgba(43, 252, 135, 1) 83%)'
      };

      // Content settings with defaults
      this.content = {
        widgetHeading: config.widgetHeading || 'Unlock Bundle Savings',
        widgetDescription: config.widgetDescription || 'Level up to save more on upgrades',
        valueBundleDescription: config.valueBundleDescription || 'Essential upgrades with great savings',
        completeBundleDescription: config.completeBundleDescription || 'Everything you need for the ultimate experience',
        eliteBundleDescription: config.eliteBundleDescription || 'Maximum savings with premium upgrades',
        includesItemsLabel: config.includesItemsLabel || 'Includes {count} items',
        moreItemsLabel: config.moreItemsLabel || '+{count} more items',
        addToCartButton: config.addToCartButton || 'Add Bundle to Cart',
        learnMoreButton: config.learnMoreButton || 'Learn more',
        originalLabel: config.originalLabel || 'Original:',
        yourPriceLabel: config.yourPriceLabel || 'Your Price:',
        mainProductLabel: config.mainProductLabel || '+ £{price} Main Product',
        saveAmountLabel: config.saveAmountLabel || 'Save £{amount}',
        discountBadgeFormat: config.discountBadgeFormat || '{percent}% OFF',
        basicBadgeLabel: config.basicBadgeLabel || 'Basic',
        loadingMessage: config.loadingMessage || 'Loading tier packages...',
        notAvailableMessage: config.notAvailableMessage || 'Bundle packages not available for this product',
        errorMessage: config.errorMessage || 'Unable to load tier packages. Please refresh the page.',
        modalPricingHeading: config.modalPricingHeading || 'Package Pricing',
        modalOriginalPriceLabel: config.modalOriginalPriceLabel || 'Original Price:',
        modalDiscountLabel: config.modalDiscountLabel || 'Discount ({percent}%):',
        modalYourPriceLabel: config.modalYourPriceLabel || 'Your Price:',
        modalIncludedHeading: config.modalIncludedHeading || "What's Included ({count} Items)",
        modalFreeLabel: config.modalFreeLabel || 'FREE',
        modalAddButton: config.modalAddButton || 'Add Bundle to Cart - £{total}',
        alertPrepareBundle: config.alertPrepareBundle || 'Unable to prepare bundle. Please refresh the page and try again.',
        alertOpenConfigurator: config.alertOpenConfigurator || 'Unable to open bundle configurator. Please try again.',
        alertFailedPrepare: config.alertFailedPrepare || 'Failed to prepare bundle. Please try again.'
      };

      // Theme-aware letter text (read from global set by bundle-drawer)
      this.letterText = this.getLetterText();
    }

    /**
     * Get theme-aware letter text based on letterPromoTheme setting
     */
    getLetterText() {
      // Try multiple sources for theme setting
      const theme = window.bundleLetterPromoTheme ||
                    window.bundleAdapter?.config?.settings?.letterPromoTheme ||
                    'generic';
      const isChristmasTheme = theme === 'christmas';
      return {
        name: isChristmasTheme ? 'Personalised Letter from Santa' : 'FREE Personalised Letter',
        description: isChristmasTheme
          ? 'A magical personalised letter from Santa with your child\'s name'
          : 'A personalised letter with your child\'s name',
        includesText: isChristmasTheme
          ? 'Includes FREE personalised Santa letter worth £9.99'
          : 'Includes FREE personalised letter worth £9.99'
      };
    }

    async init() {
      try {
        // Safety check: verify product is eligible for bundles
        const upsellIndicator = document.getElementById('UpsellIndicator');
        const upsellsAvailable = upsellIndicator?.dataset.upsellProductsAvailable === "true";

        if (!upsellsAvailable) {
          console.info('[TierProgressWidget] Product not eligible for bundles - widget should not have rendered');
          this.container.style.display = 'none';
          throw new Error('Product not eligible for bundles');
        }

        await this.buildTierPackages();

        if (!this.packages) {
          throw new Error('Failed to build tier packages');
        }

        // Check if we have at least one valid package
        if (Object.keys(this.packages).length === 0) {
          throw new Error('No valid packages could be built - required items not available');
        }
      } catch (error) {
        console.error('[TierPackages] Init failed:', error);
        throw error;
      }
    }

    /**
     * Build tier packages using SHARED logic from bundle drawer
     */
    async buildTierPackages() {
      try {
        this.loadingProducts = true;

        if (!window.bundleAdapter) {
          throw new Error('bundleAdapter not available');
        }

        const eligibleAddons = await window.bundleAdapter.loadEligibleAddons(this.productData);
        const packages = this.buildRecommendedPackagesFromEligibleAddons(eligibleAddons);

        if (!packages.showPackages) {
          this.packages = {};
        } else {
          this.packages = {};

          // Store Standard Package first (always available)
          if (packages.standardPackage) {
            this.packages[packages.standardPackage.key] = packages.standardPackage;
          }

          // Store packages using their actual key property (e.g., "value-bundle", "complete-bundle")
          if (packages.package1) {
            this.packages[packages.package1.key] = packages.package1;
          }
          if (packages.package2) {
            this.packages[packages.package2.key] = packages.package2;
          }
        }

        this.loadingProducts = false;
      } catch (error) {
        console.error('[TierPackages] Error building packages:', error);
        this.loadingProducts = false;
        throw error;
      }
    }

    /**
     * Build dynamic packages from all available addons when preset packages don't apply
     * Ensures every product has bundle options, even if they don't match preset requirements
     */
    buildDynamicPackagesFromAvailable(eligibleAddons) {
      const BUNDLE_CATEGORIES = ['performance', 'personalization', 'protection', 'gifting'];

      // Adapter already filters items via ageFilter - just collect all eligible addons
      // No duplicate filtering needed here
      const allAddons = [];
      for (const category of BUNDLE_CATEGORIES) {
        if (eligibleAddons[category]) {
          eligibleAddons[category].forEach(addon => {
            // Skip free items (alwaysIncluded or price = 0)
            if (!addon.alwaysIncluded && addon.price > 0) {
              // Skip out of stock items
              if (addon.available === false) return;
              allAddons.push(addon);
            }
          });
        }
      }

      // Even if no paid addons, still show Standard Package
      if (allAddons.length === 0) {
        const standardPackage = {
          key: 'standard-package',
          name: 'Standard Package',
          tier: 'standard',
          items: [],
          tierValue: 0,
          subtotal: 0,
          discount: 0,
          discountPercent: 0,
          total: 0,
          savings: 0,
          badge: null,
          description: 'Get your ride-on with a FREE personalised Santa letter',
          icon: 'shopping-bag',
          features: []
        };
        return { showPackages: true, standardPackage: standardPackage };
      }

      // Sort by price
      allAddons.sort((a, b) => (a.price || 0) - (b.price || 0));

      // Strategy: Split addons into two groups
      // Package 1: Lower-priced items (first half)
      // Package 2: Higher-priced items (second half + some from first half)

      const midpoint = Math.ceil(allAddons.length / 2);
      const package1Items = allAddons.slice(0, midpoint);
      const package2Items = allAddons.slice(0, allAddons.length); // Package 2 gets everything

      // Calculate totals with spend-based discount logic
      const calculatePackage = (items, name) => {
        const tierValue = items.reduce((sum, item) => sum + (item.tierValue || 0), 0);
        const subtotal = items.reduce((sum, item) => sum + (item.price || 0), 0);

        let tier = 'basic';
        let discount = 0;
        let discountPercent = '0%';

        // SPEND-BASED TIER CALCULATION:
        // £50-£149 → 15% off (value-bundle)
        // £150-£249 → 25% off (complete-bundle)
        // £250+ → 30% off (elite-bundle)
        if (subtotal >= 250) {
          tier = 'elite-bundle';
          discount = 0.30;
          discountPercent = '30%';
        } else if (subtotal >= 150) {
          tier = 'complete-bundle';
          discount = 0.25;
          discountPercent = '25%';
        } else if (subtotal >= 50) {
          tier = 'value-bundle';
          discount = 0.15;
          discountPercent = '15%';
        } else {
          tier = 'basic';
          discount = 0;
          discountPercent = '0%';
        }

        const total = subtotal * (1 - discount);
        const savings = subtotal * discount;

        return {
          key: name.toLowerCase().replace(' ', '-'),
          name,
          tier,
          items,
          tierValue,
          subtotal,
          discount,
          discountPercent: parseInt(discountPercent),
          total,
          savings,
          badge: discountPercent !== '0%' ? this.content.discountBadgeFormat.replace('{percent}', parseInt(discountPercent)) : this.content.basicBadgeLabel,
          description: name === 'Value Bundle' ? this.content.valueBundleDescription :
                      name === 'Complete Bundle' ? this.content.completeBundleDescription :
                      name === 'Elite Bundle' ? this.content.eliteBundleDescription :
                      this.content.completeBundleDescription,
          icon: name === 'Value Bundle' ? 'shopping-bag' :
                name === 'Complete Bundle' ? 'shield' :
                name === 'Elite Bundle' ? 'star' :
                'shield',
          features: items.map(item => item.name)
        };
      };

      // Build Standard Package (base product + free Santa letter)
      const standardPackage = {
        key: 'standard-package',
        name: 'Standard Package',
        tier: 'standard',
        items: [],
        tierValue: 0,
        subtotal: 0,
        discount: 0,
        discountPercent: 0,
        total: 0,
        savings: 0,
        badge: null,
        description: 'Get your ride-on with a FREE personalised Santa letter',
        icon: 'shopping-bag',
        features: []
      };

      const result = {
        showPackages: true,
        standardPackage: standardPackage,
        package1: calculatePackage(package1Items, 'Value Bundle'),
        package2: calculatePackage(package2Items, 'Complete Bundle')
      };

      return result;
    }

    /**
     * Build recommended packages using SHARED function from bundle drawer
     * No longer duplicates logic - calls window.bundleDrawer.buildRecommendedPackages()
     */
    buildRecommendedPackagesFromEligibleAddons(eligibleAddons) {
      // Use shared function from bundle drawer if available
      if (window.bundleDrawer && typeof window.bundleDrawer.buildRecommendedPackages === 'function') {
        return window.bundleDrawer.buildRecommendedPackages(eligibleAddons);
      }

      // Fallback: if bundle drawer not loaded yet, use local implementation
      console.warn('[TierProgressWidget] Bundle drawer not loaded, using fallback logic');
      return this.buildRecommendedPackagesFallback(eligibleAddons);
    }

    /**
     * Fallback implementation (same as bundle drawer)
     * Only used if bundle drawer hasn't loaded yet
     */
    buildRecommendedPackagesFallback(eligibleAddons) {
      const BUNDLE_CATEGORIES = ['performance', 'personalization', 'protection', 'gifting'];
      const totalEligible = Object.values(eligibleAddons).reduce((sum, arr) => sum + arr.length, 0);

      if (totalEligible === 0) {
        return { showPackages: false };
      }

      // Helper function to find item by handle across all categories
      const findItemByHandle = (handle) => {
        for (const category of BUNDLE_CATEGORIES) {
          if (eligibleAddons[category]) {
            const item = eligibleAddons[category].find(a => a.handle === handle);
            if (item) return item;
          }
        }
        return null;
      };

      // Find specific items by handle (EXACT SAME as bundle drawer)
      const numberPlate = findItemByHandle('number-plate-addon');
      const evaWheels = findItemByHandle('upgrade-to-soft-eva-wheels');
      const leatherSeat = findItemByHandle('upgrade-to-padded-leather-style-seat');
      const parentalRemote = findItemByHandle('upgrade-to-parental-remote-version');
      const batteryWarranty = findItemByHandle('riiroo-12-month-ride-on-battery-warranty');
      const comprehensiveWarranty = findItemByHandle('riiroo-extended-warranty');
      const giftBow = findItemByHandle('riiroo-gift-bow-in-silk-material-perfect-for-ride-on-car-motorbikes-1');
      const assemblyService = findItemByHandle('t1-assembly-service') ||
                             findItemByHandle('riiroo-ride-on-assembly-service') ||
                             findItemByHandle('t3-assembly-service');

      // Get helmet - adapter already filtered to show only the appropriate one via ageFilter
      // Just find whichever helmet is in eligibleAddons (kids OR adult, not both)
      const helmet = findItemByHandle('onemoto-uno-series-helmet-and-goggles-bundle-kids-edition') ||
                     findItemByHandle('onemoto-uno-series-helmet-and-goggles-bundle-adult-edition');

      // BUILD VALUE BUNDLE (Entry-level upsell)
      // Strategy: Add best value items to create compelling entry package
      const package1Items = [];

      // Priority 1: Battery warranty (great value, most want this)
      if (batteryWarranty) {
        package1Items.push(batteryWarranty);
      }

      // Priority 2: Number plate (personalization, kids love it)
      if (numberPlate) {
        package1Items.push(numberPlate);
      }

      // Priority 3: Comfort upgrade (EVA wheels preferred, leather seat as alternative)
      if (evaWheels) {
        package1Items.push(evaWheels);
      } else if (leatherSeat) {
        package1Items.push(leatherSeat);
      }

      // Priority 4: Helmet (safety - add to Value Bundle too!)
      if (helmet) {
        package1Items.push(helmet);
      }

      // BUILD COMPLETE BUNDLE (Premium offering)
      // Strategy: Everything from Value + premium protection + convenience items
      const package2Items = [];

      // Start with comprehensive warranty OR battery warranty (prefer comprehensive)
      if (comprehensiveWarranty) {
        package2Items.push(comprehensiveWarranty);
      } else if (batteryWarranty) {
        package2Items.push(batteryWarranty);
      }

      // Add parental remote if available (premium safety feature)
      if (parentalRemote) {
        package2Items.push(parentalRemote);
      }

      // Add number plate if available
      if (numberPlate) {
        package2Items.push(numberPlate);
      }

      // Add BOTH comfort upgrades if available (complete package!)
      if (evaWheels) {
        package2Items.push(evaWheels);
      }
      if (leatherSeat) {
        package2Items.push(leatherSeat);
      }

      // Add helmet (safety essential)
      if (helmet) {
        package2Items.push(helmet);
      }

      // Add assembly service (convenience)
      if (assemblyService) {
        package2Items.push(assemblyService);
      }

      // Add gift bow (presentation)
      if (giftBow) {
        package2Items.push(giftBow);
      }

      // Calculate packages (EXACT SAME logic as bundle drawer)
      const calculatePackage = (items, name) => {
        const tierValue = items.reduce((sum, item) => sum + (item.tierValue || 0), 0);
        const subtotal = items.reduce((sum, item) => sum + (item.price || 0), 0);

        let tier = 'basic';
        let discount = 0;
        let discountPercent = '0%';

        if (subtotal >= 250) {
          tier = 'elite-bundle';
          discount = 0.30;
          discountPercent = '30%';
        } else if (subtotal >= 150) {
          tier = 'complete-bundle';
          discount = 0.25;
          discountPercent = '25%';
        } else if (subtotal >= 50) {
          tier = 'value-bundle';
          discount = 0.15;
          discountPercent = '15%';
        }

        const total = subtotal * (1 - discount);
        const savings = subtotal * discount;

        return {
          key: name.toLowerCase().replace(' ', '-'),
          name,
          tier,
          items,
          tierValue,
          subtotal,
          discount,
          discountPercent: parseInt(discountPercent),
          total,
          savings,
          badge: discountPercent !== '0%' ? this.content.discountBadgeFormat.replace('{percent}', parseInt(discountPercent)) : this.content.basicBadgeLabel,
          description: name === 'Value Bundle' ? this.content.valueBundleDescription :
                      name === 'Complete Bundle' ? this.content.completeBundleDescription :
                      name === 'Elite Bundle' ? this.content.eliteBundleDescription :
                      this.content.completeBundleDescription,
          icon: name === 'Value Bundle' ? 'shopping-bag' :
                name === 'Complete Bundle' ? 'shield' :
                name === 'Elite Bundle' ? 'star' :
                'shield',
          features: items.map(item => item.name)
        };
      };

      // Build Standard Package (base product + free Santa letter)
      const standardPackage = {
        key: 'standard-package',
        name: 'Standard Package',
        tier: 'standard',
        items: [], // No paid addons (Santa letter shown separately as FREE)
        tierValue: 0,
        subtotal: 0,
        discount: 0,
        discountPercent: 0,
        total: 0,
        savings: 0,
        badge: null,
        description: 'Get your ride-on with a FREE personalised Santa letter',
        icon: 'shopping-bag',
        features: []
      };

      // Always show packages (no fallback needed)
      const result = {
        showPackages: true,
        standardPackage: standardPackage,
        package1: calculatePackage(package1Items, 'Value Bundle'),
        package2: calculatePackage(package2Items, 'Complete Bundle')
      };

      return result;
    }


    render() {
      try {
        // Refresh letter text in case theme was set after construction
        this.letterText = this.getLetterText();

        const html = this.buildWidgetHTML() + this.buildModalsHTML();
        this.setContent(html);
        this.attachEventListeners();
        this.setupVariantChangeListener();

        // Check parent stock and disable buttons if unavailable (async, don't block render)
        this.validateParentStockOnRender().catch(error => {
          console.error('[TierPackages] Stock validation error:', error);
        });
      } catch (error) {
        console.error('[TierPackages] Render failed:', error);
        const errorHTML = `
          <div style="padding: var(--sp-5); text-align: center; color: rgb(var(--color-drawer-text) / 0.7);">
            <p>${this.content.errorMessage}</p>
          </div>
        `;
        this.setContent(errorHTML);
      }
    }

    /**
     * Listen for variant changes and update product images
     */
    setupVariantChangeListener() {
      // Remove existing listener if any
      if (this._variantChangeHandler) {
        document.removeEventListener('variant:change', this._variantChangeHandler);
      }

      this._variantChangeHandler = (event) => {
        const variant = event.detail?.variant;
        if (variant && variant.id !== this.selectedVariantId) {
          this.selectedVariantId = variant.id;
          this.updateProductImages(variant);
        }
      };

      document.addEventListener('variant:change', this._variantChangeHandler);
    }

    /**
     * Update product images and variant info in all tier cards and modals when variant changes
     */
    updateProductImages(variant) {
      if (!variant) return;

      // Find image by media ID or get from variant
      let newImageUrl = '';
      if (variant.featured_media?.id) {
        const mediaElement = document.querySelector(`[data-media-id="${variant.featured_media.id}"] img`);
        newImageUrl = mediaElement?.src || variant.featured_image?.src || '';
      } else if (variant.featured_image) {
        newImageUrl = variant.featured_image.src || variant.featured_image;
      }

      // Fallback to main gallery image
      if (!newImageUrl) {
        const mainGalleryImg = document.querySelector('media-gallery img, .product-media img');
        newImageUrl = mainGalleryImg?.src || this.productData?.featured_image || '';
      }

      // Update images in widget cards
      if (newImageUrl) {
        const productImages = this.container.querySelectorAll('.pw-tier-card img[alt]');
        productImages.forEach(img => {
          img.src = newImageUrl;
        });
      }

      // Update images in modals
      if (newImageUrl) {
        const modalImages = document.querySelectorAll('.pw-tier-modal-product-image');
        modalImages.forEach(img => {
          img.src = newImageUrl;
        });
      }

      // Update variant title (e.g., colour name)
      const variantOption = variant.title !== 'Default Title' ? variant.title : '';

      // Update in widget cards
      const variantLabels = this.container.querySelectorAll('.pw-tier-variant-label');
      variantLabels.forEach(label => {
        label.textContent = variantOption;
        label.style.display = variantOption ? 'block' : 'none';
      });

      // Update in modals
      const modalVariantLabels = document.querySelectorAll('.pw-tier-modal-variant-label');
      modalVariantLabels.forEach(label => {
        label.textContent = variantOption;
        label.style.display = variantOption ? 'block' : 'none';
      });
    }

    /**
     * Build main widget HTML
     */
    buildWidgetHTML() {
      if (!this.packages) {
        return `
          <div style="padding: var(--sp-5); text-align: center; background: rgb(var(--color-drawer-background)); border: 2px solid rgb(var(--color-drawer-text) / 0.12); border-radius: var(--rounded-card);">
            <div style="display: inline-block; width: 2rem; height: 2rem; border: 3px solid rgb(var(--color-drawer-text) / 0.2); border-top-color: rgb(var(--color-drawer-text)); border-radius: 50%; animation: spin 0.8s linear infinite;"></div>
            <p style="margin-top: var(--sp-3); color: rgb(var(--color-drawer-text) / 0.7);">${this.content.loadingMessage}</p>
            <style>@keyframes spin { to { transform: rotate(360deg); } }</style>
          </div>
        `;
      }

      // Check if we have at least one package to show
      if (Object.keys(this.packages).length === 0) {
        return `
          <div style="padding: var(--sp-5); text-align: center; background: rgb(var(--color-drawer-background)); border: 2px solid rgb(var(--color-drawer-text) / 0.12); border-radius: var(--rounded-card);">
            <p style="color: rgb(var(--color-drawer-text) / 0.7); margin: 0;">${this.content.notAvailableMessage}</p>
          </div>
        `;
      }

      const packageCount = Object.keys(this.packages).length;
      const totalSlides = packageCount + 1; // +1 for intro card

      // Light container colors
      const containerBg = 'rgb(var(--color-drawer-background))';
      const containerBorder = 'rgb(var(--color-drawer-text) / 0.12)';

      return `
        <!-- Main Container Card (light theme) -->
        <div style="background: ${containerBg}; border: 2px solid ${containerBorder}; border-radius: var(--rounded-card); padding: var(--sp-4); position: relative; overflow: hidden;">

          <!-- Header -->
          <div style="text-align: center; margin-bottom: var(--sp-4);">
            <h3 style="font-family: var(--font-heading-family); font-weight: var(--font-heading-weight); font-size: var(--text-lg); line-height: 1.2; color: rgb(var(--color-drawer-text)); margin: 0 0 var(--sp-1d5) 0;">
              ${this.content.widgetHeading}
            </h3>
            <p style="font-size: var(--text-sm); line-height: 1.4; color: rgb(var(--color-drawer-text) / 0.7); margin: 0;">
              ${this.content.widgetDescription}
            </p>
          </div>

          <!-- Tier Cards Track (Grid on desktop, Carousel on mobile) -->
          <div class="pw-tier-cards-track" style="display: flex; overflow-x: auto; scroll-snap-type: x mandatory; gap: var(--sp-3); -webkit-overflow-scrolling: touch; scrollbar-width: none; margin: 0 calc(var(--sp-4) * -1); padding: 0 var(--sp-4); margin-bottom: var(--sp-4);">
            ${this.renderIntroCard()}
            ${Object.values(this.packages).filter(pkg => pkg !== null).map((pkg, index) => this.renderTierCard(pkg, index)).join('')}
          </div>

          <!-- Carousel Dots (Mobile only) - positioned at bottom of container -->
          <div class="pw-carousel-dots" style="display: flex; align-items: center; justify-content: center; gap: var(--sp-2);">
            ${Array.from({ length: totalSlides }).map((_, index) => `
              <button
                class="pw-carousel-dot${index === 0 ? ' active' : ''}"
                data-slide="${index}"
                aria-label="Go to ${index === 0 ? 'overview' : `package ${index}`}"
                style="width: 8px; height: 8px; border-radius: 50%; border: none; background: rgb(var(--color-drawer-text) / 0.2); cursor: pointer; padding: 0; transition: all 0.3s ease;"
              ></button>
            `).join('')}
          </div>
        </div>

        <style>
          /* Hide scrollbar but keep functionality */
          .pw-tier-cards-track::-webkit-scrollbar {
            display: none;
          }

          /* Desktop: Grid layout (3 columns on large, 2 on tablet) */
          @media (min-width: 1024px) {
            .pw-tier-cards-track {
              display: grid !important;
              grid-template-columns: repeat(3, 1fr) !important;
              align-items: start !important;
              overflow-x: visible !important;
              scroll-snap-type: none !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            .pw-carousel-dots { display: none !important; }
          }

          @media (min-width: 768px) and (max-width: 1023px) {
            .pw-tier-cards-track {
              display: grid !important;
              grid-template-columns: repeat(2, 1fr) !important;
              align-items: start !important;
              overflow-x: visible !important;
              scroll-snap-type: none !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            .pw-carousel-dots { display: none !important; }
          }

          /* Mobile: Horizontal scroll carousel */
          @media (max-width: 767px) {
            .pw-tier-card, .pw-tier-intro-card {
              flex: 0 0 calc(100% - var(--sp-6));
              scroll-snap-align: center;
              scroll-snap-stop: always;
            }
            .pw-tier-pricing { height: auto !important; }
            .pw-tier-items { min-height: auto !important; }
            .pw-tier-intro-card { display: flex !important; }
          }

          /* Hide intro card on desktop - show grid of tier cards only */
          @media (min-width: 768px) {
            .pw-tier-intro-card { display: none !important; }
          }

          /* Carousel dot active state */
          .pw-carousel-dot.active {
            background: rgb(var(--color-drawer-text)) !important;
            width: 24px !important;
            border-radius: 4px !important;
          }

          /* Dark card button hover - ensure white text on dark fill */
          .pw-tier-card--dark .button--blur:hover .btn-text,
          .pw-tier-card--dark .button--blur:focus .btn-text {
            color: #fff !important;
          }
        </style>
      `;
    }

    /**
     * Check if a tier should be highlighted (dark card)
     */
    shouldHighlightTier(tierPackage, index) {
      const setting = this.highlightTier;

      if (setting === 'none') return false;
      if (!tierPackage || !tierPackage.key) return false; // Safety check for undefined key
      if (setting === 'value' && tierPackage.key.includes('value')) return true;
      if (setting === 'complete' && tierPackage.key.includes('complete')) return true;
      if (setting === 'position-1' && index === 0) return true;
      if (setting === 'position-2' && index === 1) return true;

      // Dynamic criteria
      if (setting === 'highest-savings' || setting === 'highest-price') {
        const packages = Object.values(this.packages).filter(p => p !== null);
        if (setting === 'highest-savings') {
          const maxSavings = Math.max(...packages.map(p => p.savings || 0));
          return tierPackage.savings === maxSavings && maxSavings > 0;
        }
        if (setting === 'highest-price') {
          const maxPrice = Math.max(...packages.map(p => p.total || 0));
          return tierPackage.total === maxPrice;
        }
      }

      return false;
    }

    /**
     * Render intro card - mobile carousel only (hidden on desktop)
     */
    renderIntroCard() {
      const packages = Object.values(this.packages).filter(pkg => pkg !== null);
      const packageCount = packages.length;

      // Get colours for styling
      const textColor = 'rgb(var(--color-drawer-text))';
      const mutedText = 'rgb(var(--color-drawer-text) / 0.7)';
      const bgSubtle = 'rgb(var(--color-drawer-text) / 0.03)';
      const iconBg = 'rgb(var(--color-drawer-text) / 0.08)';

      // Benefits list with SVG icons (no emojis)
      const checkIcon = `<svg style="width: 16px; height: 16px; flex-shrink: 0; margin-top: 2px; color: rgb(var(--color-base-button));" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>`;

      const benefits = [
        { label: 'Bundle & Save up to 30%' },
        { label: 'Free Personalised Letter' },
        { label: 'Ready-Made Packages' }
      ];

      return `
        <div
          class="pw-tier-intro-card"
          style="padding: var(--sp-4); display: none; flex-direction: column; justify-content: space-between; align-items: stretch; text-align: center; scroll-snap-align: center; scroll-snap-stop: always;"
        >
          <!-- Icon -->
          <div style="width: 3rem; height: 3rem; background: ${iconBg}; border-radius: var(--rounded-card); display: flex; align-items: center; justify-content: center; margin: 0 auto var(--sp-3) auto;">
            <svg style="width: 1.5rem; height: 1.5rem; color: ${textColor};" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"></path>
            </svg>
          </div>

          <!-- Title -->
          <h4 style="font-family: var(--font-heading-family); font-weight: var(--font-heading-weight); font-size: var(--text-base); color: ${textColor}; margin: 0 0 var(--sp-1d5) 0;">
            Choose Your Bundle
          </h4>

          <!-- Description -->
          <p style="font-size: var(--text-xs); color: ${mutedText}; margin: 0 0 var(--sp-4) 0; line-height: 1.4;">
            ${packageCount} ready-made bundles or build your own
          </p>

          <!-- Benefits List - Compact with SVG icons -->
          <ul style="list-style: none; padding: 0; margin: 0 0 var(--sp-4) 0; display: flex; flex-direction: column; gap: var(--sp-2); width: 100%;">
            ${benefits.map(benefit => `
              <li style="display: flex; gap: var(--sp-2); text-align: left; align-items: center;">
                ${checkIcon}
                <div style="font-size: var(--text-xs); font-weight: 500; color: ${textColor};">${benefit.label}</div>
              </li>
            `).join('')}
          </ul>

          <!-- Build Your Own Button -->
          <button
            type="button"
            class="button button--secondary pw-build-custom-bundle"
            is="hover-button"
            style="width: 100%; margin-bottom: var(--sp-3);"
          >
            <span class="btn-fill" data-fill></span>
            <span class="btn-text">Build Your Own Bundle</span>
          </button>

          <!-- Swipe hint -->
          <div style="display: flex; align-items: center; justify-content: center; gap: var(--sp-1d5); color: ${mutedText};">
            <span style="font-size: var(--text-2xs); font-weight: 500;">Or swipe to see packages</span>
            <svg style="width: 12px; height: 12px;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
            </svg>
          </div>
        </div>
      `;
    }

    /**
     * Render individual tier card
     */
    renderTierCard(tierPackage, index = 0) {
      // Check if this is the Standard Package
      const isStandardPackage = tierPackage.key === 'standard-package';

      // If Standard Package, use simplified rendering
      if (isStandardPackage) {
        return this.renderStandardPackageCard(tierPackage);
      }

      const isDark = this.shouldHighlightTier(tierPackage, index);
      const cardClass = isDark ? 'pw-tier-card pw-tier-card--dark' : 'pw-tier-card';
      const primaryBtnClass = isDark ? 'button button--secondary pw-tier-add-to-cart' : 'button button--primary pw-tier-add-to-cart';

      // Conditional colours based on dark/light mode
      const cardBg = isDark ? 'rgb(var(--color-drawer-text))' : 'rgb(var(--color-drawer-text) / 0.02)';
      const cardBorder = isDark ? 'rgb(var(--color-drawer-text))' : 'rgb(var(--color-drawer-text) / 0.12)';
      const textColor = isDark ? 'rgb(var(--color-drawer-background))' : 'rgb(var(--color-drawer-text))';
      const mutedText = isDark ? 'rgb(var(--color-drawer-background) / 0.7)' : 'rgb(var(--color-drawer-text) / 0.7)';
      const subtleText = isDark ? 'rgb(var(--color-drawer-background) / 0.6)' : 'rgb(var(--color-drawer-text) / 0.6)';
      const fadedText = isDark ? 'rgb(var(--color-drawer-background) / 0.5)' : 'rgb(var(--color-drawer-text) / 0.5)';
      const iconBg = isDark ? 'rgb(var(--color-drawer-background) / 0.15)' : 'rgb(var(--color-drawer-text) / 0.08)';
      const pricingBg = isDark ? 'rgb(var(--color-drawer-background) / 0.1)' : 'rgb(var(--color-drawer-text) / 0.04)';
      const badgeBg = isDark ? 'rgb(var(--color-drawer-background))' : 'rgb(var(--color-drawer-text))';
      const badgeText = isDark ? 'rgb(var(--color-drawer-text))' : 'rgb(var(--color-drawer-background))';

      // Get product image URL - find selected variant's media or fallback to main gallery
      let productImage = '';
      const selectedVariant = this.selectedVariantId
        ? this.productData?.variants?.find(v => v.id === this.selectedVariantId)
        : null;

      if (selectedVariant?.featured_media?.id) {
        const mediaElement = document.querySelector(`[data-media-id="${selectedVariant.featured_media.id}"] img`);
        productImage = mediaElement?.src || '';
      }
      if (!productImage) {
        const mainGalleryImg = document.querySelector('media-gallery img, .product-media img');
        productImage = mainGalleryImg?.src
          || this.productData?.featured_image
          || (this.productData?.images && this.productData.images[0])
          || '';
      }

      const productTitle = this.productData?.title || 'Your Ride-On';
      const variantTitle = selectedVariant?.title !== 'Default Title' ? selectedVariant?.title : '';

      return `
        <div
          class="${cardClass}"
          data-tier="${tierPackage.key}"
          data-button_hover="standard"
          style="background: ${cardBg}; border: 2px solid ${cardBorder}; border-radius: var(--rounded-card); padding: var(--sp-4); display: flex; flex-direction: column; transition: all 0.2s ease; cursor: pointer;"
        >

          <!-- Header: Icon + Bundle Name & Description -->
          <div style="display: flex; align-items: flex-start; gap: var(--sp-2); margin-bottom: var(--sp-3);">
            <div style="display: flex; align-items: center; justify-content: center; width: 2.5rem; height: 2.5rem; background: ${iconBg}; border-radius: var(--rounded-card); flex-shrink: 0;">
              ${this.getTierIcon(tierPackage.icon, textColor)}
            </div>
            <div style="flex: 1; display: flex; flex-direction: column; justify-content: center;">
              <div style="display: flex; align-items: center; justify-content: space-between; gap: var(--sp-2); margin-bottom: var(--sp-0d5);">
                <h4 style="font-family: var(--font-heading-family); font-weight: var(--font-heading-weight); font-size: var(--text-sm); color: ${textColor}; margin: 0;">
                  ${tierPackage.name}
                </h4>
                ${tierPackage.badge !== this.content.basicBadgeLabel ? `
                  <div style="padding: 2px 6px; background: ${badgeBg}; color: ${badgeText}; font-size: 9px; font-weight: 700; border-radius: var(--rounded-button); line-height: 1.2; flex-shrink: 0;">
                    ${tierPackage.badge}
                  </div>
                ` : ''}
              </div>
              <p style="font-size: var(--text-2xs); color: ${mutedText}; margin: 0; line-height: 1.3;">
                ${tierPackage.description}
              </p>
            </div>
          </div>

          <!-- Main Product Display -->
          <div style="display: flex; gap: var(--sp-2); padding: var(--sp-2); margin-bottom: var(--sp-3); background: ${isDark ? 'rgb(var(--color-drawer-background) / 0.08)' : 'rgb(var(--color-drawer-text) / 0.03)'}; border-radius: var(--rounded-card);">
            ${productImage ? `
              <img src="${productImage}"
                   alt="${productTitle}"
                   style="width: 44px; height: 44px; object-fit: cover; border-radius: var(--rounded-card);"
                   loading="lazy">
            ` : `
              <div style="width: 44px; height: 44px; background: ${iconBg}; border-radius: var(--rounded-card); display: flex; align-items: center; justify-content: center;">
                <svg style="width: 20px; height: 20px; color: ${mutedText};" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
              </div>
            `}
            <div style="flex: 1; display: flex; flex-direction: column; justify-content: center;">
              <span style="font-size: 9px; font-weight: 500; color: ${mutedText}; text-transform: uppercase; letter-spacing: 0.03em; margin-bottom: 1px;">Main Product</span>
              <h5 style="font-size: var(--text-xs); font-weight: 600; margin: 0; color: ${textColor}; line-height: 1.3;">
                ${productTitle}
              </h5>
              <span class="pw-tier-variant-label" style="font-size: var(--text-2xs); color: ${mutedText}; margin-top: 1px; ${variantTitle ? '' : 'display: none;'}">${variantTitle || ''}</span>
            </div>
          </div>

          <!-- Items List -->
          <div class="pw-tier-items" style="display: flex; flex-direction: column; flex: 1; margin-bottom: var(--sp-3);">
            <div style="font-size: var(--text-2xs); font-weight: 600; color: ${textColor}; margin-bottom: var(--sp-1d5); text-transform: uppercase; letter-spacing: 0.05em;">
              ${this.content.includesItemsLabel.replace('{count}', tierPackage.items.length + 1)}
            </div>
            <ul class="pw-tier-items-list" style="list-style: none; padding: 0; margin: 0; display: grid; grid-template-columns: 1fr; gap: var(--sp-1); color: ${mutedText};">
              ${tierPackage.items.slice(0, 3).map(item => `
                <li style="font-size: var(--text-2xs); display: flex; align-items: flex-start; gap: var(--sp-1d5);">
                  <svg style="width: 12px; height: 12px; flex-shrink: 0; margin-top: 2px; color: ${textColor};" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <span style="line-height: 1.4;">${item.name}</span>
                </li>
              `).join('')}
              <!-- Personalised Letter - always included -->
              <li style="font-size: var(--text-2xs); display: flex; align-items: center; gap: var(--sp-1d5);">
                <svg style="width: 12px; height: 12px; flex-shrink: 0; margin-top: 2px; color: ${textColor};" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span style="line-height: 1.4;">${this.letterText.name}</span>
                <span style="padding: 2px 6px; background: ${badgeBg}; color: ${badgeText}; font-size: 9px; font-weight: 700; border-radius: var(--rounded-button); line-height: 1.2; flex-shrink: 0;">FREE</span>
              </li>
              ${tierPackage.items.length > 3 ? `
                <li style="padding-left: calc(12px + var(--sp-1d5)); margin-top: var(--sp-0d5);">
                  <a
                    href="#"
                    class="pw-tier-learn-more"
                    data-tier="${tierPackage.key}"
                    aria-controls="tier-modal-${tierPackage.key}"
                    style="display: inline-flex; align-items: center; gap: var(--sp-1); font-size: var(--text-2xs); font-weight: 500; color: ${isDark ? 'rgb(var(--color-drawer-background) / 0.85)' : 'rgb(var(--color-drawer-text) / 0.7)'}; text-decoration: none;"
                  >
                    <span>+ ${tierPackage.items.length - 3} more included</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style="flex-shrink: 0;">
                      <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </a>
                </li>
              ` : ''}
            </ul>
          </div>

          <!-- Pricing Breakdown -->
          <div class="pw-tier-pricing" style="margin-bottom: var(--sp-3); padding: var(--sp-3); background: ${pricingBg}; border-radius: 8px; display: flex; flex-direction: column; gap: var(--sp-1d5);">
            <!-- Main Product row -->
            <div style="display: flex; align-items: center; justify-content: space-between;">
              <span style="font-size: var(--text-2xs); color: ${mutedText};">Main Product</span>
              <span style="font-size: var(--text-xs); font-weight: 500; color: ${textColor};">£${(this.productData.price / 100).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>
            </div>

            <!-- Add-ons Subtotal row -->
            <div style="display: flex; align-items: center; justify-content: space-between;">
              <span style="font-size: var(--text-2xs); color: ${mutedText};">Add-ons (${tierPackage.items.length + 1} items)</span>
              <span style="font-size: var(--text-xs); font-weight: 500; color: ${textColor};">£${tierPackage.subtotal.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>
            </div>

            <!-- Bundle Savings row (only if discount) -->
            ${tierPackage.savings > 0 ? `
              <div style="display: flex; align-items: center; justify-content: space-between;">
                <span style="font-size: var(--text-2xs); color: rgb(34, 197, 94); font-weight: 500;">Bundle Savings (${tierPackage.discountPercent}%)</span>
                <span style="font-size: var(--text-xs); font-weight: 600; color: rgb(34, 197, 94);">-£${tierPackage.savings.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>
              </div>
            ` : ''}

            <!-- Divider -->
            <div style="border-top: 1px solid ${isDark ? 'rgb(var(--color-drawer-background) / 0.15)' : 'rgb(var(--color-drawer-text) / 0.1)'}; margin: var(--sp-0d5) 0;"></div>

            <!-- Package Total row -->
            <div style="display: flex; align-items: baseline; justify-content: space-between;">
              <span style="font-size: var(--text-xs); font-weight: 600; color: ${textColor};">Package Total</span>
              <span style="font-size: var(--text-xl); font-weight: 700; line-height: 1; color: ${textColor};">£${(() => {
                const mainPrice = this.productData.price / 100;
                const packageTotal = mainPrice + tierPackage.total;
                return packageTotal.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
              })()}</span>
            </div>

            <!-- Savings badge (only if discount) -->
            ${tierPackage.savings > 0 ? `
              <div style="display: flex; justify-content: flex-end; margin-top: var(--sp-0d5);">
                <div style="padding: 2px 6px; background: ${badgeBg}; border-radius: var(--rounded-button); display: flex; align-items: center; justify-content: center;">
                  <span style="font-size: 9px; font-weight: 700; color: ${badgeText}; text-align: center;">You save £${tierPackage.savings.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>
                </div>
              </div>
            ` : ''}
          </div>

          <!-- Buttons -->
          <div style="display: flex; flex-direction: column; gap: var(--sp-2d5);">
            <button
              type="button"
              class="button button--secondary ${isDark ? 'button--blur' : ''} pw-tier-add-to-cart"
              is="hover-button"
              data-tier="${tierPackage.key}"
              style="width: 100%;${isDark ? ` --color-button-gradient: ${this.buttonColors.gradient}; --color-button-background: ${this.hexToRgb(this.buttonColors.background)}; --color-button-text: ${this.hexToRgb(this.buttonColors.text)}; --color-button-border: ${this.hexToRgb(this.buttonColors.background)};` : ''}"
              onclick="event.stopPropagation()"
            >
              <span class="btn-fill" data-fill></span>
              <span class="btn-text">${this.content.addToCartButton}</span>
            </button>

            <a
              href="#"
              class="pw-tier-learn-more"
              data-tier="${tierPackage.key}"
              aria-controls="tier-modal-${tierPackage.key}"
              style="display: flex; align-items: center; justify-content: center; gap: var(--sp-1d5); width: 100%; font-size: var(--text-xs); font-weight: 500; color: ${isDark ? 'rgb(var(--color-drawer-background))' : 'rgb(var(--color-drawer-text) / 0.7)'}; text-decoration: none; padding: var(--sp-1d5) 0;"
            >
              <span>${this.content.learnMoreButton}</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </a>
          </div>

        </div>
      `;
    }

    /**
     * Render Standard Package card (simplified version)
     */
    renderStandardPackageCard(tierPackage) {
      // Card styling
      const cardBg = 'rgb(var(--color-drawer-text) / 0.02)';
      const cardBorder = 'rgb(var(--color-drawer-text) / 0.12)';
      const textColor = 'rgb(var(--color-drawer-text))';
      const mutedText = 'rgb(var(--color-drawer-text) / 0.7)';
      const iconBg = 'rgb(var(--color-drawer-text) / 0.08)';
      const pricingBg = 'rgb(var(--color-drawer-text) / 0.04)';
      const badgeBg = 'rgb(var(--color-drawer-text))';
      const badgeText = 'rgb(var(--color-drawer-background))';

      // Get product details
      let productImage = '';
      const selectedVariant = this.selectedVariantId
        ? this.productData?.variants?.find(v => v.id === this.selectedVariantId)
        : null;

      if (selectedVariant?.featured_media?.id) {
        const mediaElement = document.querySelector(`[data-media-id="${selectedVariant.featured_media.id}"] img`);
        productImage = mediaElement?.src || '';
      }
      if (!productImage) {
        const mainGalleryImg = document.querySelector('media-gallery img, .product-media img');
        productImage = mainGalleryImg?.src
          || this.productData?.featured_image
          || (this.productData?.images && this.productData.images[0])
          || '';
      }

      const productTitle = this.productData?.title || 'Your Ride-On';
      const variantTitle = selectedVariant?.title !== 'Default Title' ? selectedVariant?.title : '';
      const mainPrice = this.productData.price / 100;

      return `
        <div
          class="pw-tier-card"
          data-tier="${tierPackage.key}"
          data-button_hover="standard"
          style="background: ${cardBg}; border: 2px solid ${cardBorder}; border-radius: var(--rounded-card); padding: var(--sp-5); display: flex; flex-direction: column; transition: all 0.2s ease; cursor: pointer;"
        >

          <!-- Header: Icon + Bundle Name & Description -->
          <div style="display: flex; align-items: flex-start; gap: var(--sp-3); margin-bottom: var(--sp-4);">
            <div style="display: flex; align-items: center; justify-content: center; width: 3rem; height: 3rem; background: ${iconBg}; border-radius: var(--rounded-card); flex-shrink: 0;">
              ${this.getTierIcon(tierPackage.icon, textColor)}
            </div>
            <div style="flex: 1; display: flex; flex-direction: column; justify-content: center;">
              <h4 style="font-family: var(--font-heading-family); font-weight: var(--font-heading-weight); font-size: var(--text-base); color: ${textColor}; margin: 0 0 var(--sp-1) 0;">
                ${tierPackage.name}
              </h4>
              <p style="font-size: var(--text-xs); color: ${mutedText}; margin: 0; line-height: 1.4;">
                ${tierPackage.description}
              </p>
            </div>
          </div>

          <!-- Main Product Display -->
          <div style="display: flex; gap: var(--sp-3); padding: var(--sp-3); margin-bottom: var(--sp-4); background: rgb(var(--color-drawer-text) / 0.03); border-radius: var(--rounded-card);">
            ${productImage ? `
              <img src="${productImage}"
                   alt="${productTitle}"
                   style="width: 56px; height: 56px; object-fit: cover; border-radius: var(--rounded-card);"
                   loading="lazy">
            ` : `
              <div style="width: 56px; height: 56px; background: ${iconBg}; border-radius: var(--rounded-card); display: flex; align-items: center; justify-content: center;">
                <svg style="width: 24px; height: 24px; color: ${mutedText};" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
              </div>
            `}
            <div style="flex: 1; display: flex; flex-direction: column; justify-content: center;">
              <span style="font-size: var(--text-2xs); font-weight: 500; color: ${mutedText}; text-transform: uppercase; letter-spacing: 0.03em; margin-bottom: 2px;">Main Product</span>
              <h5 style="font-size: var(--text-sm); font-weight: 600; margin: 0; color: ${textColor}; line-height: 1.3;">
                ${productTitle}
              </h5>
              <span class="pw-tier-variant-label" style="font-size: var(--text-xs); color: ${mutedText}; margin-top: 2px; ${variantTitle ? '' : 'display: none;'}">${variantTitle || ''}</span>
            </div>
          </div>

          <!-- FREE Personalised Letter Highlight -->
          <div style="background: ${pricingBg}; border: 1px solid ${cardBorder}; border-radius: var(--rounded-card); padding: var(--sp-4); margin-bottom: var(--sp-4); flex: 1;">
            <div style="display: flex; align-items: center; justify-content: space-between; gap: var(--sp-2);">
              <div style="display: flex; align-items: center; gap: var(--sp-2);">
                <svg style="width: 20px; height: 20px; color: ${textColor}; flex-shrink: 0;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <span style="font-size: var(--text-sm); font-weight: 600; color: ${textColor};">${this.letterText.name}</span>
              </div>
              <div style="padding: 4px 10px; background: ${badgeBg}; color: ${badgeText}; font-size: var(--text-xs); font-weight: 700; border-radius: var(--rounded-button); line-height: 1.2; flex-shrink: 0;">
                FREE
              </div>
            </div>
          </div>

          <!-- Simplified Pricing -->
          <div style="margin-bottom: var(--sp-4); padding: var(--sp-4); background: ${pricingBg}; border-radius: 8px; display: flex; flex-direction: column; gap: var(--sp-2);">
            <!-- Package Total (same as main product price) -->
            <div style="display: flex; align-items: baseline; justify-content: space-between;">
              <span style="font-size: var(--text-sm); font-weight: 600; color: ${textColor};">Package Total</span>
              <span style="font-size: 1.5rem; font-weight: 700; line-height: 1; color: ${textColor};">£${mainPrice.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>
            </div>
            <p style="font-size: var(--text-xs); color: ${mutedText}; margin: 0; line-height: 1.4;">
              ${this.letterText.includesText}
            </p>
          </div>

          <!-- Buttons -->
          <div style="display: flex; flex-direction: column; gap: var(--sp-3);">
            <button
              type="button"
              class="button button--secondary pw-tier-add-to-cart"
              is="hover-button"
              data-tier="${tierPackage.key}"
              style="width: 100%;"
              onclick="event.stopPropagation()"
            >
              <span class="btn-fill" data-fill></span>
              <span class="btn-text">Select Package</span>
            </button>

            <a
              href="#"
              class="pw-tier-learn-more"
              data-tier="${tierPackage.key}"
              aria-controls="tier-modal-${tierPackage.key}"
              style="display: flex; align-items: center; justify-content: center; gap: var(--sp-1d5); width: 100%; font-size: var(--text-sm); font-weight: 500; color: rgb(var(--color-drawer-text) / 0.7); text-decoration: none; padding: var(--sp-2) 0;"
            >
              <span>${this.content.learnMoreButton}</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </a>
          </div>

        </div>
      `;
    }

    /**
     * Get tier icon SVG
     */
    getTierIcon(iconType, iconColor) {
      const icons = {
        'shopping-bag': `
          <svg style="width: 1.5rem; height: 1.5rem; color: ${iconColor};" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path>
          </svg>
        `,
        'shield': `
          <svg style="width: 1.5rem; height: 1.5rem; color: ${iconColor};" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
          </svg>
        `,
        'star': `
          <svg style="width: 1.5rem; height: 1.5rem; color: ${iconColor};" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"></path>
          </svg>
        `
      };

      return icons[iconType] || icons['shopping-bag'];
    }

    /**
     * Build modals HTML for available tiers (only 2 now - Value and Complete)
     */
    buildModalsHTML() {
      if (!this.packages) return '';

      // Iterate over all packages dynamically using their keys
      return Object.values(this.packages)
        .filter(pkg => pkg !== null)
        .map(pkg => this.buildTierModal(pkg))
        .join('');
    }

    /**
     * Build Learn More modal for a tier
     */
    buildTierModal(tierPackage) {
      // Get product image and info (same logic as widget cards)
      let productImage = '';
      const selectedVariant = this.selectedVariantId
        ? this.productData?.variants?.find(v => v.id === this.selectedVariantId)
        : null;

      if (selectedVariant?.featured_media?.id) {
        const mediaElement = document.querySelector(`[data-media-id="${selectedVariant.featured_media.id}"] img`);
        productImage = mediaElement?.src || '';
      }
      if (!productImage) {
        const mainGalleryImg = document.querySelector('media-gallery img, .product-media img');
        productImage = mainGalleryImg?.src
          || this.productData?.featured_image
          || (this.productData?.images && this.productData.images[0])
          || '';
      }

      const productTitle = this.productData?.title || 'Your Ride-On';
      const variantTitle = selectedVariant?.title !== 'Default Title' ? selectedVariant?.title : '';
      const mainProductPrice = this.productData.price / 100;
      const packageTotal = mainProductPrice + tierPackage.total;

      return `
        <x-modal id="tier-modal-${tierPackage.key}" class="x-modal drawer z-30 fixed bottom-0 left-0 h-full w-full pointer-events-none" role="dialog" aria-modal="true" aria-label="${tierPackage.name} Details" hidden>
          <overlay-element class="overlay fixed-modal invisible opacity-0 fixed bottom-0 left-0 w-full h-screen pointer-events-none" aria-controls="tier-modal-${tierPackage.key}" aria-expanded="false"></overlay-element>
          <div class="drawer__inner z-10 absolute top-0 flex flex-col w-full h-full overflow-hidden">
            <gesture-element class="drawer__header flex items-center justify-between opacity-0 invisible relative">
              <span class="drawer__title heading font-bold title-md">${tierPackage.name}</span>
              <button class="button button--secondary button--close drawer__close hidden sm:flex items-center justify-center" type="button" is="hover-button" aria-controls="tier-modal-${tierPackage.key}" aria-expanded="false" aria-label="Close">
                <span class="btn-fill" data-fill></span>
                <span class="btn-text">
                  <svg class="icon icon-close icon-sm" viewBox="0 0 20 20" stroke="currentColor" fill="none">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 15L15 5M5 5L15 15"></path>
                  </svg>
                </span>
              </button>
            </gesture-element>
            <div class="drawer__content opacity-0 invisible flex flex-col h-full grow shrink">
              <div class="drawer__scrollable relative flex flex-col gap-5 md:gap-7 grow shrink">

                <!-- Hero Section -->
                <div class="text-center">
                  <div style="display: inline-flex; align-items: center; justify-content: center; width: 4rem; height: 4rem; background: rgb(var(--color-drawer-text)); border-radius: 50%; margin-bottom: 1rem;">
                    ${this.getTierIcon(tierPackage.icon, 'rgb(var(--color-drawer-background))')}
                  </div>
                  ${tierPackage.badge && tierPackage.badge !== this.content.basicBadgeLabel ? `
                    <div style="display: flex; align-items: center; justify-content: center; padding: 0.25rem 0.75rem; background: rgb(var(--color-drawer-text)); color: rgb(var(--color-drawer-background)); font-weight: 700; font-size: var(--text-base); border-radius: var(--rounded-button); margin: 0 auto 0.75rem auto; width: fit-content;">
                      ${tierPackage.badge}
                    </div>
                  ` : ''}
                  <h3 class="heading font-bold text-xl md:text-2xl" style="margin-bottom: 0.75rem;">
                    ${tierPackage.name}
                  </h3>
                  <p class="text-base text-opacity" style="max-width: 500px; margin: 0 auto;">
                    ${tierPackage.description}
                  </p>
                </div>

                <!-- Main Product Card -->
                <div style="padding: 1.5rem; background: rgb(var(--color-drawer-text) / 0.03); border-radius: var(--rounded-card); border: 1px solid rgb(var(--color-drawer-text) / 0.08);">
                  <div style="display: flex; gap: 1rem; align-items: center;">
                    ${productImage ? `
                      <img src="${productImage}"
                           alt="${productTitle}"
                           class="pw-tier-modal-product-image"
                           style="width: 80px; height: 80px; object-fit: cover; border-radius: var(--rounded-card); flex-shrink: 0;"
                           loading="lazy">
                    ` : `
                      <div style="width: 80px; height: 80px; background: rgb(var(--color-drawer-text) / 0.08); border-radius: var(--rounded-card); display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                        <svg style="width: 32px; height: 32px; color: rgb(var(--color-drawer-text) / 0.4);" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                        </svg>
                      </div>
                    `}
                    <div style="flex: 1;">
                      <span style="display: block; font-size: var(--text-xs); font-weight: 600; color: rgb(var(--color-drawer-text) / 0.6); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.25rem;">Main Product</span>
                      <h4 class="font-bold text-base" style="margin: 0 0 0.25rem 0; line-height: 1.3;">
                        ${productTitle}
                      </h4>
                      <span class="pw-tier-modal-variant-label text-sm text-opacity" style="${variantTitle ? '' : 'display: none;'}">${variantTitle || ''}</span>
                    </div>
                  </div>
                </div>

                <!-- Full Pricing Breakdown (matches widget cards) -->
                <div style="padding: 1.5rem; background: rgb(var(--color-drawer-text) / 0.03); border-radius: var(--rounded-card); border: 1px solid rgb(var(--color-drawer-text) / 0.08);">
                  <h4 class="font-bold text-base" style="margin-bottom: 1rem;">${this.content.modalPricingHeading}</h4>

                  <!-- Main Product row -->
                  <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem;">
                    <span class="text-sm text-opacity">Main Product</span>
                    <span class="text-base font-medium">£${mainProductPrice.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>
                  </div>

                  <!-- Add-ons Subtotal row -->
                  <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem;">
                    <span class="text-sm text-opacity">Add-ons (${tierPackage.items.length + 1} items)</span>
                    <span class="text-base font-medium">£${tierPackage.subtotal.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>
                  </div>

                  <!-- Bundle Savings row (only if discount) -->
                  ${tierPackage.savings > 0 ? `
                    <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem;">
                      <span class="text-sm font-medium" style="color: rgb(34, 197, 94);">Bundle Savings (${tierPackage.discountPercent}%)</span>
                      <span class="text-base font-semibold" style="color: rgb(34, 197, 94);">-£${tierPackage.savings.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>
                    </div>
                  ` : ''}

                  <!-- Divider -->
                  <div style="border-top: 1px solid rgb(var(--color-drawer-text) / 0.12); margin: 0.75rem 0;"></div>

                  <!-- Package Total row -->
                  <div style="display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 0.75rem;">
                    <span class="text-base font-bold">Package Total</span>
                    <span class="text-2xl font-bold">£${packageTotal.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>
                  </div>

                  <!-- Savings badge (only if discount) -->
                  ${tierPackage.savings > 0 ? `
                    <div style="display: flex; justify-content: flex-end;">
                      <div style="padding: 4px 12px; background: rgb(var(--color-drawer-text)); border-radius: var(--rounded-button); display: flex; align-items: center; justify-content: center;">
                        <span style="font-size: var(--text-xs); font-weight: 700; color: rgb(var(--color-drawer-background)); text-align: center;">You save £${tierPackage.savings.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  ` : ''}
                </div>

                <!-- Included Items -->
                <div style="padding: 1.5rem; border: 2px solid rgb(var(--color-drawer-text) / 0.12); border-radius: var(--rounded-card); background: rgb(var(--color-drawer-background));">
                  <h4 class="font-bold text-base" style="margin-bottom: 1rem;">
                    ${this.content.modalIncludedHeading.replace('{count}', tierPackage.items.length + 1)}
                  </h4>
                  <ul class="text-sm" style="list-style: none; padding: 0; margin: 0;">
                    ${tierPackage.items.map(item => `
                      <li style="padding: 0.75rem 0; display: flex; align-items: start; gap: 0.75rem; border-top: 1px solid rgb(var(--color-drawer-text) / 0.08);">
                        ${item.featured_image || item.image ? `
                          <img src="${item.featured_image || item.image}"
                               alt="${item.name}"
                               style="width: 60px; height: 60px; object-fit: cover; border-radius: var(--rounded-card); flex-shrink: 0;"
                               loading="lazy">
                        ` : `
                          <div style="width: 60px; height: 60px; background: rgb(var(--color-drawer-text) / 0.08); border-radius: var(--rounded-card); display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                            <svg style="width: 24px; height: 24px; color: rgb(var(--color-drawer-text) / 0.4);" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                            </svg>
                          </div>
                        `}
                        <div style="flex: 1; display: flex; flex-direction: column; justify-content: center;">
                          <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.25rem;">
                            <svg style="flex-shrink: 0; width: 1rem; height: 1rem; color: rgb(var(--color-base-button));" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                            <div class="font-semibold">${item.name}</div>
                          </div>
                          <div style="color: rgb(var(--color-drawer-text) / 0.7); font-size: var(--text-xs); margin-bottom: 0.25rem;">${item.description || ''}</div>
                          <div style="font-weight: 600; font-size: var(--text-sm);">
                            ${item.price === 0 ? this.content.modalFreeLabel : '£' + item.price.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                          </div>
                        </div>
                      </li>
                    `).join('')}
                    <!-- Personalised Letter - always included -->
                    <li style="padding: 0.75rem 0; display: flex; align-items: start; gap: 0.75rem; border-top: 1px solid rgb(var(--color-drawer-text) / 0.08);">
                      <div style="width: 60px; height: 60px; background: ${window.bundleLetterPromoTheme === 'christmas' ? 'linear-gradient(135deg, rgb(220, 38, 38) 0%, rgb(239, 68, 68) 100%)' : 'linear-gradient(135deg, rgb(107, 114, 128) 0%, rgb(156, 163, 175) 100%)'}; border-radius: var(--rounded-card); display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                        <svg style="width: 32px; height: 32px; color: white;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                        </svg>
                      </div>
                      <div style="flex: 1; display: flex; flex-direction: column; justify-content: center;">
                        <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.25rem;">
                          <svg style="flex-shrink: 0; width: 1rem; height: 1rem; color: rgb(var(--color-base-button));" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                          </svg>
                          <div class="font-semibold">${this.letterText.name}</div>
                        </div>
                        <div style="color: rgb(var(--color-drawer-text) / 0.7); font-size: var(--text-xs); margin-bottom: 0.25rem;">${this.letterText.description}</div>
                        <div style="font-weight: 600; font-size: var(--text-sm); color: rgb(34, 197, 94);">
                          ${this.content.modalFreeLabel}
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>

                <!-- Add to Cart CTA -->
                <div style="padding: 1.5rem; background: rgb(var(--color-drawer-text) / 0.03); border-radius: var(--rounded-card); text-align: center;">
                  <button
                    type="button"
                    class="button button--primary pw-tier-add-to-cart"
                    is="hover-button"
                    data-tier="${tierPackage.key}"
                    style="width: 100%; max-width: 400px;"
                  >
                    <span class="btn-fill" data-fill></span>
                    <span class="btn-text">${this.content.modalAddButton.replace('{total}', packageTotal.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 }))}</span>
                  </button>
                </div>

              </div>
            </div>
          </div>
        </x-modal>
      `;
    }

    /**
     * Attach event listeners
     */
    attachEventListeners() {
      // Add to Cart buttons
      const addToCartButtons = this.container.querySelectorAll('.pw-tier-add-to-cart');
      addToCartButtons.forEach(button => {
        button.addEventListener('click', (e) => this.handleAddToCart(e));
      });

      // Build Your Own Bundle button (intro card)
      const buildCustomButton = this.container.querySelector('.pw-build-custom-bundle');
      if (buildCustomButton) {
        buildCustomButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleBuildCustomBundle();
        });
      }

      // Learn More buttons - prevent event bubbling to card click handler
      const learnMoreButtons = this.container.querySelectorAll('.pw-tier-learn-more');
      learnMoreButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          // Stop propagation so card click handler doesn't trigger
          e.stopPropagation();
          // Let the x-modal system handle opening the modal via aria-controls
        });
      });

      // Make entire cards clickable to open modal
      const tierCards = this.container.querySelectorAll('.pw-tier-card');
      tierCards.forEach(card => {
        card.addEventListener('click', (e) => this.handleCardClick(e));
      });

      // Carousel navigation setup (for mobile)
      this.setupCarouselNavigation();
    }

    /**
     * Setup carousel navigation with scroll tracking and dot indicators
     */
    setupCarouselNavigation() {
      const track = this.container.querySelector('.pw-tier-cards-track');
      const dots = this.container.querySelectorAll('.pw-carousel-dot');
      const cards = this.container.querySelectorAll('.pw-tier-card, .pw-tier-intro-card');

      if (!track || dots.length === 0 || cards.length === 0) {
        return; // Not in carousel mode or no elements found
      }

      // Dot click handlers
      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          const card = cards[index];
          if (card) {
            card.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'center'
            });
          }
        });
      });

      // Intersection Observer to update active dot based on visible card
      const observerOptions = {
        root: track,
        threshold: 0.5, // Consider card visible when 50% is in view
        rootMargin: '0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const cardIndex = Array.from(cards).indexOf(entry.target);
            if (cardIndex !== -1) {
              // Update active dot
              dots.forEach((dot, index) => {
                if (index === cardIndex) {
                  dot.classList.add('active');
                } else {
                  dot.classList.remove('active');
                }
              });
            }
          }
        });
      }, observerOptions);

      // Observe all cards
      cards.forEach(card => observer.observe(card));

      // Store observer reference for cleanup
      this._carouselObserver = observer;
    }

    /**
     * Handle card click - open modal
     */
    handleCardClick(event) {
      const card = event.currentTarget;
      const tierKey = card.dataset.tier;

      // Find the modal for this tier
      const modalId = `tier-modal-${tierKey}`;
      const modal = document.getElementById(modalId);

      if (modal) {
        // Find the Learn More button and trigger it
        const learnMoreButton = card.querySelector('.pw-tier-learn-more');
        if (learnMoreButton) {
          learnMoreButton.click();
        }
      }
    }

    /**
     * Validate parent stock on initial render and disable buttons if unavailable
     */
    async validateParentStockOnRender() {
      // FETCH fresh product data from API to get variants (bypass Liquid caching)
      const stockCheck = await this.checkParentStockViaAPI();

      if (!stockCheck.available) {
        console.warn('[TierPackages] Parent product out of stock - HIDING tier widget');

        // Hide the entire widget since parent is out of stock
        this.container.style.display = 'none';

        // Emit event so alternatives widget knows to show
        this.emit('tier-progress:parent-out-of-stock', {
          message: stockCheck.message
        });
      }
    }

    /**
     * Check parent stock by fetching fresh data from Shopify API
     * Bypasses Liquid caching issues
     * @returns {Promise<{available: boolean, message: string}>}
     */
    async checkParentStockViaAPI() {
      if (!this.productData || !this.productData.handle) {
        console.warn('[TierPackages] No product handle available');
        return {
          available: true,
          message: 'No product handle available'
        };
      }

      try {
        const response = await fetch(`/products/${this.productData.handle}.js`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const productData = await response.json();

        return this.checkVariantsStock(productData.variants, productData.title);

      } catch (error) {
        console.error('[TierPackages] Error fetching product data:', error);
        return {
          available: true, // Don't block on error
          message: 'Error fetching product data'
        };
      }
    }

    /**
     * Check if parent product has stock available
     * Validates that parent product (ride-on) has at least one variant in stock
     * @returns {{available: boolean, message: string}}
     */
    checkParentStock() {
      if (!this.productData || !this.productData.variants) {
        console.warn('[TierPackages] No parent product data available for stock check');
        return {
          available: true, // Don't block if data not available
          message: 'No parent product data available'
        };
      }

      const { variants, title } = this.productData;

      return this.checkVariantsStock(variants, title);
    }

    /**
     * Check stock across variants
     * @param {Array} variants - Variant array
     * @param {string} title - Product title
     * @returns {{available: boolean, message: string}}
     */
    checkVariantsStock(variants, title) {
      if (!variants || variants.length === 0) {
        return {
          available: true,
          message: 'No variants to check'
        };
      }

      // Check if ANY variant has stock available
      const hasStockInAnyVariant = variants.some(variant => {
        // Check if variant is available
        if (variant.available === true) {
          return true;
        }

        // Check inventory policy - if 'continue', can sell when out of stock
        if (variant.inventory_policy === 'continue') {
          return true;
        }

        // Check if inventory management is disabled
        if (!variant.inventory_management || variant.inventory_management === null) {
          return true;
        }

        // Check if variant has inventory quantity > 0
        if (variant.inventory_quantity && variant.inventory_quantity > 0) {
          return true;
        }

        return false;
      });

      if (hasStockInAnyVariant) {
        return {
          available: true,
          message: `Parent product "${title}" has stock available`
        };
      } else {
        return {
          available: false,
          message: `Parent product "${title}" is out of stock`
        };
      }
    }

    /**
     * Handle Build Your Own Bundle button click
     * Opens bundle drawer in custom configurator mode
     */
    async handleBuildCustomBundle() {
      // Ensure ProductLinkingManager is loaded
      if (!window.ProductLinkingManager) {
        console.error('[TierPackages] ProductLinkingManager not loaded');
        alert(this.content.alertPrepareBundle);
        return;
      }

      // CHECK PARENT STOCK FIRST
      const parentStockCheck = await this.checkParentStockViaAPI();

      if (!parentStockCheck.available) {
        console.warn('[TierPackages] Parent product out of stock:', parentStockCheck.message);
        alert('Parent product is out of stock. Cannot add bundle.');
        return;
      }

      // Ensure parent product is in cart first
      const linkingManager = new ProductLinkingManager({
        productId: this.productData.id,
        productHandle: this.productData.handle,
        productVariantId: this.productData.variants && this.productData.variants[0]
          ? this.productData.variants[0].id
          : null,

        // Success callback - parent is ready
        onReady: (productLinkId, cartItem) => {
          // Signal to skip package selection and go straight to custom configurator
          window.skipToCustomConfigurator = true;

          // Open bundle drawer
          const bundleDrawer = document.querySelector('drawer-element#BundleDrawer');
          if (bundleDrawer) {
            bundleDrawer.show();

            this.emit('tier-packages:custom-bundle-started', {
              productId: this.productData.id,
              productLinkId: productLinkId
            });
          } else {
            console.error('[TierPackages] Bundle drawer not found');
            alert(this.content.alertOpenConfigurator);
          }
        },

        // Cancel callback
        onCancel: () => {
          // User cancelled
        },

        // Error callback
        onError: (error) => {
          console.error('[TierPackages] Error ensuring parent in cart:', error);
          alert(this.content.alertFailedPrepare);
        }
      });

      // Trigger the parent product check
      linkingManager.ensureParentInCart();
    }

    /**
     * Handle Add to Cart button click
     */
    async handleAddToCart(event) {
      event.preventDefault();
      event.stopPropagation();

      const tierKey = event.currentTarget.dataset.tier;
      const tierPackage = this.packages[tierKey];

      if (!tierPackage) {
        console.error('[TierPackages] Package not found:', tierKey);
        return;
      }

      // Ensure ProductLinkingManager is loaded
      if (!window.ProductLinkingManager) {
        console.error('[TierPackages] ProductLinkingManager not loaded');
        alert(this.content.alertPrepareBundle);
        return;
      }

      // CHECK PARENT STOCK FIRST - Don't allow bundle if parent is out of stock
      const parentStockCheck = await this.checkParentStockViaAPI();

      if (!parentStockCheck.available) {
        console.warn('[TierPackages] Parent product out of stock:', parentStockCheck.message);
        alert('Parent product is out of stock. Cannot add bundle.');
        return;
      }

      // Ensure parent product is in cart first
      const linkingManager = new ProductLinkingManager({
        productId: this.productData.id,
        productHandle: this.productData.handle,
        productVariantId: this.productData.variants && this.productData.variants[0]
          ? this.productData.variants[0].id
          : null,

        // Success callback - parent is ready
        onReady: (productLinkId, cartItem) => {
          // Store package globally for bundle drawer
          window.preSelectedTierPackage = {
            ...tierPackage,
            productId: this.productData.id,
            productVariantId: cartItem.variant_id,
            productLinkId: productLinkId  // Pass productLinkId to bundle drawer
          };

          // Open bundle drawer
          const bundleDrawer = document.querySelector('drawer-element#BundleDrawer');
          if (bundleDrawer) {
            bundleDrawer.show();

            // Emit event
            this.emit('tier-packages:bundle-added', {
              tier: tierKey,
              packageName: tierPackage.name,
              items: tierPackage.items.length,
              total: tierPackage.total,
              productLinkId: productLinkId
            });
          } else {
            console.error('[TierPackages] Bundle drawer not found');
            alert(this.content.alertOpenConfigurator);
          }
        },

        // Cancel callback
        onCancel: () => {
          // Logging removed
        },

        // Error callback
        onError: (error) => {
          console.error('[TierPackages] Error ensuring parent in cart:', error);
          alert(this.content.alertFailedPrepare);
        }
      });

      // Trigger the parent product check
      linkingManager.ensureParentInCart();
    }

    /**
     * Convert hex colour to RGB format for CSS custom properties
     * @param {string} hex - Hex colour like '#171717' or '171717'
     * @returns {string} RGB values like '23 23 23'
     */
    hexToRgb(hex) {
      // Remove # if present
      hex = hex.replace(/^#/, '');

      // Parse hex values
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);

      // Return space-separated RGB values (CSS custom property format)
      return `${r} ${g} ${b}`;
    }

    destroy() {
      // Clean up carousel observer
      if (this._carouselObserver) {
        this._carouselObserver.disconnect();
        this._carouselObserver = null;
      }

      // Clean up variant change listener
      if (this._variantChangeHandler) {
        document.removeEventListener('variant:change', this._variantChangeHandler);
        this._variantChangeHandler = null;
      }

      super.destroy();
    }
  }

  // Expose to global scope
  if (!window.ProductWidgets || !window.ProductWidgets.widgets) {
    window.ProductWidgets = window.ProductWidgets || {};
    window.ProductWidgets.widgets = window.ProductWidgets.widgets || {};
  }

  window.ProductWidgets.widgets.TierProgressWidget = TierProgressWidget;

})(window);
